import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../../supabaseClient';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';

const { FiPlus, FiEdit2, FiTrash2, FiLogOut, FiEye, FiAlertCircle } = FiIcons;

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      if (!isSupabaseConfigured()) {
        setError('Supabase is not configured. Please connect to Supabase in the chat box.');
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      if (data) setPosts(data);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Unable to load posts: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        const { error } = await supabase.from('posts').delete().eq('id', id);
        if (error) throw error;
        fetchPosts();
      } catch (err) {
        alert('Erreur lors de la suppression: ' + err.message);
      }
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8 pt-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Tableau de bord</h1>
          <div className="flex gap-4">
            <Link
              to="/admin/editor"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-2" /> Nouveau Post
            </Link>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
            >
              <SafeIcon icon={FiLogOut} className="mr-2" /> Déconnexion
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 flex items-start">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 dark:text-blue-200 font-medium">Configuration Requise</p>
              <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="p-4 text-gray-600 dark:text-gray-300 font-semibold">Titre</th>
                <th className="p-4 text-gray-600 dark:text-gray-300 font-semibold">Statut</th>
                <th className="p-4 text-gray-600 dark:text-gray-300 font-semibold">Date</th>
                <th className="p-4 text-gray-600 dark:text-gray-300 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                  <td className="p-4 text-gray-800 dark:text-white font-medium">{post.title}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      post.published 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {post.published ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="p-4 text-gray-600 dark:text-gray-400">
                    {format(new Date(post.created_at), 'dd/MM/yyyy')}
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <Link
                      to={`/blog/${post.slug}`}
                      target="_blank"
                      className="inline-block p-2 text-gray-500 hover:text-blue-600"
                      title="Voir"
                    >
                      <SafeIcon icon={FiEye} />
                    </Link>
                    <Link
                      to={`/admin/editor/${post.id}`}
                      className="inline-block p-2 text-blue-500 hover:text-blue-700"
                      title="Éditer"
                    >
                      <SafeIcon icon={FiEdit2} />
                    </Link>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-red-500 hover:text-red-700"
                      title="Supprimer"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {posts.length === 0 && (
            <div className="p-8 text-center text-gray-500">Aucun article trouvé.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;